/**
 * Wallet abstraction layer
 *
 * Decouples components from Privy and wagmi so that:
 * - Production: PrivyWalletProvider reads from real Privy + wagmi hooks
 * - E2E test mode (VITE_TEST_MODE=true): MockWalletProvider reads from window.__TEST_WALLET_STATE__
 * - Dev without Privy (no providers): useWallet() returns static disconnected state
 *
 * Components should ONLY use useWallet() — never import usePrivy / useAccount directly.
 */

import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type WalletContextValue = {
	/** Whether the user has completed authentication and wallet is connected */
	isConnected: boolean;
	/** Checksummed wallet address (e.g. "0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045") */
	address: string | undefined;
	/** Active chain ID (e.g. 16600 for 0G testnet) */
	chainId: number | undefined;
	/** Open login modal / trigger wallet connection */
	login: () => void;
	/** End session — Privy logout */
	logout: () => void;
	/** Disconnect wallet and end session */
	disconnect: () => void;
	/** Retrieve a JWT access token for authenticated API calls */
	getAccessToken: () => Promise<string | null>;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const noop = () => {};

const noopAsync = async () => null;

const disconnectedFallback: WalletContextValue = {
	isConnected: false,
	address: undefined,
	chainId: undefined,
	login: noop,
	logout: noop,
	disconnect: noop,
	getAccessToken: noopAsync,
};

export const WalletContext = createContext<WalletContextValue>(disconnectedFallback);

/**
 * Read wallet state and actions.
 * Returns disconnected fallback when used outside a WalletProvider.
 */
export function useWallet(): WalletContextValue {
	return useContext(WalletContext);
}

// ---------------------------------------------------------------------------
// Test provider — reads from window.__TEST_WALLET_STATE__
// ---------------------------------------------------------------------------

/** Shape that Playwright sets on window before navigating */
export type TestWalletState = {
	address: string;
	chainId: number;
	connected?: boolean;
};

declare global {
	interface Window {
		__TEST_WALLET_STATE__?: TestWalletState;
	}
}

export function MockWalletProvider({ children }: { children: ReactNode }) {
	const testState = typeof window !== "undefined" ? window.__TEST_WALLET_STATE__ : undefined;
	const [connected, setConnected] = useState(testState?.connected ?? false);

	const login = useCallback(() => {
		setConnected(true);
	}, []);

	const logout = useCallback(() => {
		setConnected(false);
	}, []);

	const getAccessToken = useCallback(async () => (connected ? "test-token" : null), [connected]);

	const value = useMemo<WalletContextValue>(
		() => ({
			isConnected: connected,
			address: connected ? testState?.address : undefined,
			chainId: testState?.chainId,
			login,
			logout,
			disconnect: logout,
			getAccessToken,
		}),
		[connected, testState?.address, testState?.chainId, login, logout, getAccessToken]
	);

	return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
